import { format } from "date-fns";

/**
 * Composable for calendar printing functionality
 */
export function useCalendarPrinting() {
  /**
   * Generate a PDF of the calendar view using the server-side API
   *
   * @param {Object} options - Calendar printing options
   * @param {Object} options.calendarDisplay - Reference to calendar display component
   * @param {Object} options.selectedStore - The currently selected store
   * @param {Function} options.showToast - Function to display toast notifications
   * @returns {Promise<void>}
   */
  const printCalendar = async ({
    calendarDisplay,
    selectedStore,
    showToast,
  }) => {
    try {
      if (!selectedStore || !selectedStore.id) {
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Please select a store to print the schedule",
          life: 3000,
        });
        return;
      }

      // Get the calendar API to determine current view dates
      const calendarApi = calendarDisplay?.getCalendarApi();
      if (!calendarApi) return;

      // Get the current date information
      const currentDate = calendarApi.getDate();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
      const isWeekView = calendarApi.view.type === "timeGridWeek";

      // Build the API URL
      let url;

      if (isWeekView) {
        // Handle week view
        const weekStart = format(calendarApi.view.activeStart, "yyyy-MM-dd");
        url = `/api/pdf/weekly-schedule?store_id=${selectedStore.id}&start_date=${weekStart}`;
      } else {
        // Handle month view (default)
        url = `/api/pdf/schedule?store_id=${selectedStore.id}&year=${year}&month=${month}`;
      }

      // Open the PDF in a new tab/window
      window.open(url, "_blank");

      showToast({
        severity: "success",
        summary: "Success",
        detail: `Calendar exported to PDF (${
          isWeekView ? "Week" : "Month"
        } view)`,
        life: 3000,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to export calendar",
        life: 3000,
      });
    }
  };

  return {
    printCalendar,
  };
}
